import React, { useState, useEffect, useRef, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

const STATUS_META = {
    available: { label: 'Available', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a' },
    busy:      { label: 'Busy',      color: '#dc2626', bg: '#fef2f2', border: '#fecaca', dot: '#dc2626' },
    break:     { label: 'On Break',  color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#d97706' },
    offline:   { label: 'Offline',   color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb', dot: '#9ca3af' },
};

function getInitials(name) { if (!name || name === '—') return '?'; return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); }
function getDisplayName(t) { return t.contact_name || t.variables?.name || t.variables?.full_name || t.identity_key || 'Unknown User'; }
function getContactPhone(t) { return t.variables?.phone_no || t.variables?.phone || t.identity_key || ''; }
function formatTime(iso) { if (!iso) return ''; return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); }
function timeAgo(iso) {
    if (!iso) return '';
    const date = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60)    return `${diff}s ago`;
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

// ── apiFetch helper ───────────────────────────────────────────────────────────
async function apiFetch(method, path, body) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${chatbotUrl}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || 'Request failed');
    return data;
}

// ── Status Selector ───────────────────────────────────────────────────────────
function StatusSelector({ current, onChange, loading }) {
    const [open, setOpen] = useState(false);
    const meta = STATUS_META[current] || STATUS_META.offline;
    const ref  = useRef(null);

    useEffect(() => {
        const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button onClick={() => setOpen(p => !p)} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:meta.bg, border:`1.5px solid ${meta.border}`, borderRadius:10, cursor:'pointer', width:'100%', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:meta.dot }}/>
                    <span style={{ fontSize:13, fontWeight:700, color:meta.color }}>{meta.label}</span>
                </div>
                {loading
                    ? <i className="bi bi-arrow-repeat" style={{ color:meta.color, fontSize:12, animation:'spin 1s linear infinite' }}></i>
                    : <i className="bi bi-chevron-down" style={{ color:meta.color, fontSize:11 }}></i>}
            </button>
            {open && (
                <div style={{ position:'absolute', top:'110%', left:0, right:0, zIndex:50, background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, boxShadow:'0 8px 24px rgba(0,0,0,0.1)', overflow:'hidden' }}>
                    {Object.entries(STATUS_META).map(([key, sm]) => (
                        <button key={key} onClick={() => { onChange(key); setOpen(false); }}
                            style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', width:'100%', border:'none', background: current===key ? sm.bg : '#fff', cursor:'pointer', fontSize:12, color:sm.color, fontWeight: current===key ? 700 : 500 }}
                            onMouseEnter={e => e.currentTarget.style.background = sm.bg}
                            onMouseLeave={e => e.currentTarget.style.background = current===key ? sm.bg : '#fff'}>
                            <div style={{ width:8, height:8, borderRadius:'50%', background:sm.dot, flexShrink:0 }}/>
                            {sm.label}
                            {current===key && <span style={{ marginLeft:'auto', fontSize:10 }}>✓</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Chat Item ─────────────────────────────────────────────────────────────────
function ChatItem({ transfer, isActive, isPending, unread, onClick }) {
    const name    = getDisplayName(transfer);
    const initials = getInitials(name);
    const channel  = transfer.channel || 'whatsapp';
    return (
        <div onClick={onClick}
            style={{ padding:'12px 16px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', background: isActive ? '#eff6ff' : '#fff', borderLeft: isActive ? '3px solid #1565c0' : '3px solid transparent', transition:'all 0.15s' }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ position:'relative', flexShrink:0 }}>
                    <div style={{ width:38, height:38, borderRadius:'50%', background: isActive ? '#1565c0' : '#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color: isActive ? '#fff' : '#1565c0' }}>
                        {initials}
                    </div>
                    <div style={{ position:'absolute', bottom:0, right:0, width:10, height:10, borderRadius:'50%', background: isPending ? '#d97706' : '#16a34a', border:'2px solid #fff' }}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <p style={{ fontSize:13, fontWeight:600, color:'#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:140, margin:0 }}>{name}</p>
                        {unread > 0 && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:10, background:'#dc2626', color:'#fff', flexShrink:0 }}>{unread}</span>}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                        <i className={`bi ${channel === 'whatsapp' ? 'bi-whatsapp' : 'bi-chat-dots'}`} style={{ fontSize:9, color:'#9ca3af' }}></i>
                        <span style={{ fontSize:10, color:'#9ca3af' }}>{channel}</span>
                        <span style={{ fontSize:10, color:'#9ca3af' }}>·</span>
                        <span style={{ fontSize:10, color: isPending ? '#d97706' : '#16a34a', fontWeight:600 }}>{isPending ? 'Waiting' : 'Active'}</span>
                        <span style={{ fontSize:10, color:'#9ca3af' }}>· {timeAgo(transfer.transferred_at)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Chat Window ───────────────────────────────────────────────────────────────
function ChatWindow({ transfer, onAccept, onResolve, isPending, wsRef, msgStoreRef: externalMsgStore }) {
    const [messages,  setMessages]  = useState([]);
    const [inputText, setInputText] = useState('');
    const [sending,   setSending]   = useState(false);
    const [expanded,  setExpanded]  = useState(false);
    const messagesEndRef             = useRef(null);
    const sessionId                  = transfer.session_id;
    const localMsgStore              = useRef({});
    const msgStoreRef                = externalMsgStore || localMsgStore;

    useEffect(() => { setMessages(msgStoreRef.current[sessionId] || []); setInputText(''); setExpanded(false); }, [sessionId]);
    useEffect(() => { if (messages.length > 0) msgStoreRef.current[sessionId] = messages; }, [messages, sessionId]);
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

    useEffect(() => {
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const base  = chatbotUrl.replace(/\/v1$/, '');
                const res   = await fetch(`${base}/v1/agent-panel/live-messages/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
                const data  = await res.json();
                if (data.success && data.data.length > 0) setMessages(data.data);
            } catch (e) { /* ignored */ }
        })();
    }, [sessionId]);

    useEffect(() => {
        const ws = wsRef?.current;
        if (!ws) return;
        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'customer_message' && data.session_id === sessionId) {
                    setMessages(prev => [...prev, { role:'user', content:data.message, time: new Date().toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }) }]);
                }
            } catch (e) { /* ignored */ }
        };
        ws.addEventListener('message', handler);
        return () => ws.removeEventListener('message', handler);
    }, [sessionId]);

    const sendMessage = async () => {
        if (!inputText.trim() || sending) return;
        const msg = inputText.trim();
        setInputText(''); setSending(true);
        setMessages(prev => [...prev, { role:'agent', content:msg, time: new Date().toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }) }]);
        if (wsRef?.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type:'agent_message', session_id:sessionId, message:msg }));
        setSending(false);
    };

    const name    = getDisplayName(transfer);
    const phone   = getContactPhone(transfer);
    const vars    = transfer.variables || {};
    const history = transfer.history   || [];

    return (
        <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#f8fafc' }}>
            {/* Header */}
            <div style={{ padding:'16px 20px', background:'#fff', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:40, height:40, borderRadius:'50%', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'#1565c0' }}>
                        {getInitials(name)}
                    </div>
                    <div>
                        <p style={{ fontSize:14, fontWeight:700, color:'#111827', margin:0 }}>{name}</p>
                        <div style={{ display:'flex', gap:8, marginTop:2 }}>
                            {phone && <span style={{ fontSize:11, color:'#9ca3af', display:'flex', alignItems:'center', gap:3 }}><i className="bi bi-telephone" style={{ fontSize:10 }}></i>{phone}</span>}
                            <span style={{ fontSize:11, color:'#9ca3af' }}>· {formatTime(transfer.transferred_at)}</span>
                        </div>
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    {isPending ? (
                        <button onClick={() => onAccept(transfer)} style={{ padding:'8px 16px', fontSize:12, fontWeight:700, color:'#fff', background:'linear-gradient(to right, #2388e0, #1565c0)', border:'none', borderRadius:8, cursor:'pointer' }}>
                            <i className="bi bi-check-lg" style={{ marginRight:4 }}></i>Accept Chat
                        </button>
                    ) : (
                        <button onClick={() => onResolve(transfer.session_id)} style={{ padding:'8px 16px', fontSize:12, fontWeight:700, color:'#16a34a', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, cursor:'pointer' }}>
                            <i className="bi bi-check2-all" style={{ marginRight:4 }}></i>Resolve
                        </button>
                    )}
                </div>
            </div>

            {/* Context */}
            <div style={{ padding:'8px 16px', background:'#eff6ff', borderBottom:'1px solid #bfdbfe', flexShrink:0 }}>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ fontSize:10, fontWeight:700, color:'#1565c0', textTransform:'uppercase' }}>Context:</span>
                    {Object.entries(vars).slice(0, 4).map(([k, v]) => (
                        <span key={k} style={{ fontSize:10, padding:'2px 8px', background:'#fff', borderRadius:6, fontFamily:'monospace', color:'#374151', border:'1px solid #bfdbfe' }}>{k}={String(v).slice(0, 15)}</span>
                    ))}
                    {transfer.reason && <span style={{ fontSize:10, color:'#1565c0' }}>Reason: {transfer.reason}</span>}
                </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:10 }}>
                {history.length > 0 && (
                    <div>
                        <button onClick={() => setExpanded(p => !p)} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, cursor:'pointer', fontSize:11, color:'#6b7280', fontWeight:600, marginBottom:8 }}>
                            <i className="bi bi-file-text" style={{ fontSize:11 }}></i> AI Conversation ({history.filter(t => t.content !== '__init__').length} turns) {expanded ? '▲' : '▼'}
                        </button>
                        {expanded && history.filter(t => t.content !== '__init__').map((turn, i) => (
                            <div key={i} style={{ display:'flex', flexDirection: turn.role==='user' ? 'row-reverse' : 'row', gap:8, marginBottom:8, alignItems:'flex-end' }}>
                                <div style={{ maxWidth:'70%', padding:'8px 12px', borderRadius: turn.role==='user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: turn.role==='user' ? '#1565c0' : '#fff', color: turn.role==='user' ? '#fff' : '#111827', fontSize:12, border:'1px solid #e5e7eb' }}>
                                    {turn.content}
                                </div>
                            </div>
                        ))}
                        <div style={{ borderBottom:'1px solid #e5e7eb', margin:'10px 0', display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:10, color:'#9ca3af', background:'#f8fafc', padding:'2px 8px', borderRadius:8 }}>Live chat started</span>
                        </div>
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div key={i} style={{ display:'flex', flexDirection: msg.role==='agent' ? 'row-reverse' : 'row', gap:8, alignItems:'flex-end' }}>
                        <div style={{ maxWidth:'70%', padding:'10px 14px', borderRadius: msg.role==='agent' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: msg.role==='agent' ? '#1565c0' : '#fff', color: msg.role==='agent' ? '#fff' : '#111827', fontSize:13, border:'1px solid #e5e7eb', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
                            {msg.content}
                            <div style={{ fontSize:9, color: msg.role==='agent' ? 'rgba(255,255,255,0.6)' : '#9ca3af', marginTop:4, textAlign:'right' }}>{msg.time}</div>
                        </div>
                    </div>
                ))}

                {messages.length === 0 && !isPending && (
                    <div style={{ textAlign:'center', color:'#9ca3af', fontSize:12, padding:20 }}>
                        <i className="bi bi-chat-dots" style={{ fontSize:24, display:'block', margin:'0 auto 8px', opacity:0.3 }}></i>
                        Chat accepted — type a message to start
                    </div>
                )}
                {isPending && (
                    <div style={{ textAlign:'center', padding:24 }}>
                        <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:10, padding:'16px 24px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:12 }}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize:20, color:'#d97706' }}></i>
                            <p style={{ fontSize:13, color:'#92400e', fontWeight:600, margin:0 }}>Accept this chat to start messaging</p>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef}/>
            </div>

            {/* Input */}
            {!isPending && (
                <div style={{ padding:'12px 16px', background:'#fff', borderTop:'1px solid #e5e7eb', flexShrink:0 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'flex-end', background:'#f8fafc', border:'1.5px solid #e5e7eb', borderRadius:14, padding:'8px 8px 8px 14px' }}>
                        <textarea value={inputText} onChange={e => setInputText(e.target.value)}
                            onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                            placeholder="Type a message... (Enter to send)" rows={1}
                            style={{ flex:1, border:'none', background:'transparent', outline:'none', resize:'none', fontSize:13, color:'#111827', maxHeight:88, fontFamily:'inherit', lineHeight:1.5, padding:'2px 0' }}/>
                        <button onClick={sendMessage} disabled={!inputText.trim() || sending}
                            style={{ width:36, height:36, borderRadius:10, border:'none', background: inputText.trim() ? '#1565c0' : '#e5e7eb', cursor: inputText.trim() ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <i className="bi bi-send-fill" style={{ fontSize:13, color:'#fff' }}></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main Agent Panel ──────────────────────────────────────────────────────────
export default function AgentPanel() {
    const [myStatus,         setMyStatus]         = useState('offline');
    const [statusLoading,    setStatusLoading]    = useState(false);
    const [pendingTransfers, setPendingTransfers] = useState([]);
    const [activeChats,      setActiveChats]      = useState({});
    const [selectedId,       setSelectedId]       = useState(null);
    const [unreadCounts,     setUnreadCounts]     = useState({});
    const [maxChats,         setMaxChats]         = useState(5);
    const [showSettings,     setShowSettings]     = useState(false);
    const [breakSeconds,     setBreakSeconds]     = useState(0);
    const [agents,           setAgents]           = useState([]);
    const wsRef       = useRef(null);
    const msgStoreRef = useRef({});
    const breakRef    = useRef(null);

    const fetchAgents = async () => {
        try { const data = await apiFetch('GET', '/agents/availability'); setAgents(data?.data || []); } catch (e) { /* ignored */ }
    };

    useEffect(() => {
        (async () => {
            try { const data = await apiFetch('GET', '/agents/availability/me'); const me = data?.data; if (me) { setMyStatus(me.status || 'offline'); setMaxChats(me.max_concurrent_chats || 5); } } catch (e) { /* ignored */ }
        })();
        fetchAgents();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const data      = await apiFetch('GET', '/agent-panel/transfers?status=active');
                const transfers = data?.data || [];
                if (transfers.length > 0) {
                    const chats = {};
                    transfers.forEach(t => { chats[t.session_id] = { ...t, _isPending: false }; });
                    setActiveChats(chats);
                    if (!selectedId) setSelectedId(transfers[0].session_id);
                    toast.success(`${transfers.length} active chat(s) restored`, { autoClose: 2000 });
                }
            } catch (e) { /* ignored */ }
        })();
    }, []);

    useEffect(() => {
        if (myStatus === 'break') { setBreakSeconds(0); breakRef.current = setInterval(() => setBreakSeconds(s => s + 1), 1000); }
        else { clearInterval(breakRef.current); setBreakSeconds(0); }
        return () => clearInterval(breakRef.current);
    }, [myStatus]);

    const connectWS = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        let userId;
        try { 
            const payload = JSON.parse(atob(token.split('.')[1]));
            userId = payload?.user_id || payload?._id || payload?.id;
        } catch (e) { return; }
        if (!userId) return;
        const base  = chatbotUrl.replace(/\/v1$/, '').replace(/\/$/, '');
        const wsUrl = base.replace(/^http/, 'ws') + `/v1/ws/agent/${userId}?token=${token}`;
        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            ws.onclose = () => { wsRef.current = null; setTimeout(() => connectWS(), 3000); };
            ws.onerror = () => ws.close();
            ws.addEventListener('message', (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'new_transfer') {
                        const vars = data.context?.variables || {};
                        const t = { ...data.context, session_id: data.session_id, transferred_at: data.timestamp, contact_name: data.context?.contact?.name || vars.name || '', variables: vars, history: data.context?.history || [] };
                        setPendingTransfers(prev => { if (prev.find(p => p.session_id === data.session_id)) return prev; return [t, ...prev]; });
                        toast(`📞 New transfer from ${t.contact_name || t.identity_key || 'User'}`, { autoClose: 6000 });
                    } else if (data.type === 'customer_message') {
                        setUnreadCounts(prev => { if (selectedId === data.session_id) return prev; return { ...prev, [data.session_id]: (prev[data.session_id] || 0) + 1 }; });
                    }
                } catch (e) { /* ignored */ }
            });
        } catch (e) { setTimeout(() => connectWS(), 3000); }
    }, []);

    useEffect(() => { connectWS(); return () => { if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); } }; }, [connectWS]);

    const handleStatusChange = async (status) => {
        setStatusLoading(true);
        try { await apiFetch('PUT', '/agents/availability', { status, note: '' }); setMyStatus(status); toast.success(`Status: ${STATUS_META[status].label}`, { autoClose: 2000 }); fetchAgents(); }
        catch (e) { setMyStatus(status); }
        finally { setStatusLoading(false); }
    };

    const handleAccept = (transfer) => {
        const sid = transfer.session_id;
        setPendingTransfers(prev => prev.filter(t => t.session_id !== sid));
        setActiveChats(prev => ({ ...prev, [sid]: transfer }));
        setSelectedId(sid);
        toast.success('Chat accepted', { autoClose: 2000 });
    };

    const handleResolve = async (sessionId) => {
        if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type:'resolve', session_id:sessionId }));
        setActiveChats(prev => { const n = { ...prev }; delete n[sessionId]; return n; });
        if (selectedId === sessionId) {
            const remaining = Object.keys(activeChats).filter(k => k !== sessionId);
            setSelectedId(remaining[0] || pendingTransfers[0]?.session_id || null);
        }
        toast.success('Chat resolved', { autoClose: 2000 });
    };

    const handleMaxChatsChange = async (val) => {
        const n = Math.max(1, Math.min(20, parseInt(val) || 5));
        setMaxChats(n);
        try { await apiFetch('PUT', '/agents/max-chats', { max_concurrent_chats: n }); toast.success(`Max chats set to ${n}`, { autoClose: 2000 }); } catch (e) { /* ignored */ }
    };

    const allChats    = [...pendingTransfers.map(t => ({ ...t, _isPending: true })), ...Object.values(activeChats).map(t => ({ ...t, _isPending: false }))];
    const activeCount = Object.keys(activeChats).length;
    const pendingCount = pendingTransfers.length;
    const formatBreak = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

    return (
        <WrapperComponent>
            <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 80px)', overflow:'hidden', fontFamily:"'Segoe UI', system-ui, sans-serif", gap:16 }}>
                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                    <div>
                        <h5 style={{ margin:0, fontSize:20, fontWeight:700, color:'#111827' }}>Agent Panel</h5>
                        <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>Manage live WhatsApp transfers</p>
                    </div>
                    <button onClick={() => setShowSettings(p => !p)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, cursor:'pointer', fontSize:12, fontWeight:600, color:'#374151' }}>
                        <i className="bi bi-gear" style={{ fontSize:13 }}></i> Settings
                    </button>
                </div>

                {showSettings && (
                    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', gap:20, flexShrink:0 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:'#374151' }}>Max concurrent chats:</label>
                        <input type="number" min={1} max={20} value={maxChats} onChange={e => handleMaxChatsChange(e.target.value)}
                            style={{ width:70, padding:'6px 10px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, fontWeight:700, color:'#111827', outline:'none', textAlign:'center' }}/>
                        <span style={{ fontSize:11, color:'#9ca3af' }}>Currently: {activeCount}/{maxChats} active</span>
                    </div>
                )}

                <div style={{ display:'flex', flex:1, gap:0, overflow:'hidden', border:'1px solid #e5e7eb', borderRadius:16, background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                    {/* Sidebar */}
                    <div style={{ width:280, flexShrink:0, borderRight:'1px solid #e5e7eb', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                        {/* Status */}
                        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e5e7eb', background:'#f8fafc' }}>
                            <StatusSelector current={myStatus} onChange={handleStatusChange} loading={statusLoading}/>
                            {myStatus === 'break' && (
                                <div style={{ marginTop:8, padding:'6px 10px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, textAlign:'center' }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:'#d97706', fontFamily:'monospace' }}>{formatBreak(breakSeconds)}</span>
                                </div>
                            )}
                            {myStatus !== 'available' && <p style={{ fontSize:10, color:'#9ca3af', marginTop:6, textAlign:'center', marginBottom:0 }}>Set Available to receive transfers</p>}
                        </div>

                        {/* Counts */}
                        <div style={{ padding:'10px 16px', borderBottom:'1px solid #e5e7eb', display:'flex', gap:12 }}>
                            <div style={{ flex:1, textAlign:'center', padding:6, background:'#fffbeb', borderRadius:8 }}>
                                <p style={{ fontSize:18, fontWeight:800, color:'#d97706', margin:0 }}>{pendingCount}</p>
                                <p style={{ fontSize:10, color:'#92400e', fontWeight:600, margin:0 }}>Pending</p>
                            </div>
                            <div style={{ flex:1, textAlign:'center', padding:6, background:'#f0fdf4', borderRadius:8 }}>
                                <p style={{ fontSize:18, fontWeight:800, color:'#16a34a', margin:0 }}>{activeCount}</p>
                                <p style={{ fontSize:10, color:'#065f46', fontWeight:600, margin:0 }}>Active</p>
                            </div>
                            <div style={{ flex:1, textAlign:'center', padding:6, background:'#eff6ff', borderRadius:8 }}>
                                <p style={{ fontSize:18, fontWeight:800, color:'#1565c0', margin:0 }}>{maxChats}</p>
                                <p style={{ fontSize:10, color:'#1e40af', fontWeight:600, margin:0 }}>Capacity</p>
                            </div>
                        </div>

                        {/* Chat list */}
                        <div style={{ flex:1, overflowY:'auto' }}>
                            {allChats.length === 0 ? (
                                <div style={{ padding:32, textAlign:'center', color:'#9ca3af' }}>
                                    <i className="bi bi-chat-dots" style={{ fontSize:28, display:'block', margin:'0 auto 8px', opacity:0.2 }}></i>
                                    <p style={{ fontSize:12, fontWeight:600, color:'#374151', margin:0 }}>No transfers yet</p>
                                    <p style={{ fontSize:11, marginTop:4, marginBottom:0 }}>Set status to Available</p>
                                </div>
                            ) : allChats.map(t => (
                                <ChatItem key={t.session_id} transfer={t} isActive={selectedId === t.session_id} isPending={t._isPending} unread={unreadCounts[t.session_id] || 0}
                                    onClick={() => { setSelectedId(t.session_id); setUnreadCounts(prev => ({ ...prev, [t.session_id]: 0 })); }}/>
                            ))}
                        </div>

                        {/* Team */}
                        <div style={{ borderTop:'1px solid #e5e7eb', padding:'12px 16px', background:'#f8fafc' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                                <span style={{ fontSize:11, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                                    <i className="bi bi-people" style={{ marginRight:5 }}></i>Team
                                </span>
                                <button onClick={fetchAgents} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}>
                                    <i className="bi bi-arrow-clockwise" style={{ fontSize:11 }}></i>
                                </button>
                            </div>
                            {agents.slice(0, 4).map(agent => {
                                const sm = STATUS_META[agent.status] || STATUS_META.offline;
                                return (
                                    <div key={agent.id || agent._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                                            <div style={{ width:24, height:24, borderRadius:'50%', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:'#1565c0' }}>
                                                {(agent.name || 'A').charAt(0).toUpperCase()}
                                            </div>
                                            <span style={{ fontSize:11, color:'#374151', fontWeight:500 }}>{agent.name || agent.agent_id}</span>
                                        </div>
                                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                                            <div style={{ width:6, height:6, borderRadius:'50%', background:sm.dot }}/>
                                            <span style={{ fontSize:10, color:sm.color, fontWeight:600 }}>{sm.label}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Chat window */}
                    <div style={{ flex:1, overflow:'hidden' }}>
                        {allChats.length === 0 && (
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', flexDirection:'column', gap:12, color:'#9ca3af' }}>
                                <i className="bi bi-chat-dots" style={{ fontSize:40, opacity:0.2 }}></i>
                                <p style={{ fontWeight:600, color:'#374151', fontSize:14, margin:0 }}>Select a chat from the sidebar</p>
                                <p style={{ fontSize:12, marginTop:4 }}>Incoming WhatsApp transfers will appear on the left</p>
                            </div>
                        )}
                        {allChats.map(t => (
                            <div key={t.session_id} style={{ display: selectedId === t.session_id ? 'flex' : 'none', flexDirection:'column', height:'100%' }}>
                                <ChatWindow transfer={t} isPending={t._isPending} onAccept={handleAccept} onResolve={handleResolve} wsRef={wsRef} msgStoreRef={msgStoreRef}/>
                            </div>
                        ))}
                    </div>
                </div>
                <ToastContainer/>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
}
